import React, { useCallback } from 'react';
import { ConfigProvider, Spin, Upload, Button, message } from "antd";
import { UploadOutlined } from '@ant-design/icons';
import enUS from "antd/locale/en_US";
import { getAssets, saveAsset, updateAsset, deleteAsset } from "service/assets";
import EditorTable from "../component/EditorTable";
import useLoading from "components/common/useLoading";
import Papa from 'papaparse';

const AssetColumns = [
  {
    title: "Asset Name",
    dataIndex: "asset_name",
    key: "asset_name",
    rule: [{ required: true }],
    editable: true,
  },
  {
    title: "Unit System",
    dataIndex: "unit_system",
    key: "unit_system",
    rule: [{ required: true }],
    editable: true,
    valueType: "select",
    valueEnum: {
      field: { text: "Field", status: "Field" },
      metric: { text: "Metric", status: "Metric" },
    },
  },
];

function WellAssetEditor(props) {
  const [LoadingIcon] = useLoading(<Spin size="large" />, 50);
  const [tableData, setTableData] = React.useState([]);

  const handleCSVUpload = (file) => {
    Papa.parse(file, {
      header: true,
      complete: async (results) => {
        if (results.errors.length > 0) {
          message.error('Error parsing CSV file');
          return;
        }

        const validData = results.data.filter(row => 
          row.asset_name && 
          row.unit_system && 
          ['field', 'metric'].includes(row.unit_system.toLowerCase())
        );

        if (validData.length === 0) {
          message.error('No valid data found in CSV');
          return;
        }

        try {
          for (let i = 0; i < validData.length; i++) {
            const row = validData[i];
            const isLastRow = i === validData.length - 1;
            
            await saveAsset(
              row,
              (data) => {
                console.log('Asset saved:', data);
                if (isLastRow) {
                  getAssets(
                    (newData) => {
                      setTableData(newData);
                      message.success(`Successfully added ${validData.length} assets`);
                    },
                    (error) => {
                      message.error('Error fetching updated data');
                    }
                  );
                }
              },
              (error) => {
                console.error('Error saving asset:', error);
                throw error;
              }
            );
          }
        } catch (error) {
          message.error('Error saving assets');
        }
      },
      error: (error) => {
        message.error('Error reading CSV file');
      }
    });
    return false;
  };

  const uploadProps = {
    accept: '.csv',
    showUploadList: false,
    beforeUpload: handleCSVUpload,
  };

  const buildQuery = useCallback(() => {
    return {};
  }, []);

  return (
    <div style={{ height: "91vh", overflowY: "auto" }}>
      <LoadingIcon />
      <ConfigProvider
        locale={enUS}
        componentSize="middle"
        theme={{
          token: {},
          components: {},
        }}
      >
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Upload CSV</Button>
          </Upload>
        </div>
        <EditorTable
          dataSource={tableData}
          columns={AssetColumns || []}
          title="Well Asset"
          dataFetcher={[
            // () => { return {};},
            buildQuery,
            (params, onSuccess, onError) => {
              getAssets((data) => {
                setTableData(data);
                onSuccess?.(data);
              }, onError);
            },
          ]}
          dataSaver={[
            (rec, onSuccess, onError)=>{
              updateAsset(rec.id, rec, onSuccess, onError);
            },
            (rec, row) => {
              const record = { ...rec };
              delete record.index;
              return record;
            },
            saveAsset,
            (rec, row) => {
              const record = { ...rec };
              delete record.index;
              delete record.id;
              return record;
            },
          ]}
          dataRemover={[deleteAsset, (record) => record.id]}
        />
      </ConfigProvider>
    </div>
  );
}

export default WellAssetEditor;
